import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, CreditCard as Edit, Trash2, Save, X, FileText, AlertCircle, ChevronDown, ChevronUp, ArrowLeft, Download } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Modal } from '@shared/components/ui/Modal';

interface Prompt {
  id: string;
  name: string;
  description: string | null;
  content: string;
  execution_order: number;
  is_active: boolean;
  output_variable: string | null;
  depends_on_steps: any[];
  created_at: string;
  updated_at: string;
}

const AdminPrompts: React.FC = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    execution_order: 1,
    is_active: true,
    output_variable: '',
    depends_on_steps: '[]'
  });
  const [saving, setSaving] = useState(false);
  const [contextVariables, setContextVariables] = useState<any>(null);
  const [personas, setPersonas] = useState<string[]>([]);
  const [showVariables, setShowVariables] = useState(true);
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadPrompts();
    loadContextVariables();
    loadPersonas();
  }, []);

  const loadContextVariables = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-context-variables`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setContextVariables(data);
      }
    } catch (error) {
      console.error('Error loading context variables:', error);
    }
  };

  const loadPersonas = async () => {
    try {
      const { data, error } = await supabase
        .from('personas')
        .select('name')
        .order('name');

      if (error) throw error;
      setPersonas(data?.map(p => p.name) || []);
    } catch (error) {
      console.error('Error loading personas:', error);
    }
  };

  const insertVariable = (variableName: string) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;

    const cursorPosition = textarea.selectionStart;
    const textBefore = formData.content.substring(0, cursorPosition);
    const textAfter = formData.content.substring(cursorPosition);
    const newContent = `${textBefore}{${variableName}}${textAfter}`;

    setFormData({ ...formData, content: newContent });

    setTimeout(() => {
      textarea.focus();
      const newPosition = cursorPosition + variableName.length + 2;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPrompts(prompts);
    } else {
      const filtered = prompts.filter(
        (prompt) =>
          prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (prompt.description && prompt.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPrompts(filtered);
    }
  }, [searchTerm, prompts]);

  const loadPrompts = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .order('execution_order', { ascending: true });

      if (error) throw error;

      setPrompts(data || []);
      setFilteredPrompts(data || []);
    } catch (error: any) {
      console.error('Error loading prompts:', error);
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPrompt(null);
    setFormData({
      name: '',
      description: '',
      content: '',
      execution_order: prompts.length + 1,
      is_active: true,
      output_variable: '',
      depends_on_steps: '[]'
    });
    setShowModal(true);
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      content: prompt.content,
      execution_order: prompt.execution_order,
      is_active: prompt.is_active,
      output_variable: prompt.output_variable || '',
      depends_on_steps: JSON.stringify(prompt.depends_on_steps, null, 2)
    });
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.content) {
      toast.error('Name and content are required');
      return;
    }

    try {
      setSaving(true);

      let dependsOnSteps = [];
      try {
        dependsOnSteps = JSON.parse(formData.depends_on_steps);
      } catch {
        toast.error('Invalid JSON format for depends_on_steps');
        return;
      }

      const promptData = {
        name: formData.name,
        description: formData.description || null,
        content: formData.content,
        execution_order: formData.execution_order,
        is_active: formData.is_active,
        output_variable: formData.output_variable || null,
        depends_on_steps: dependsOnSteps,
        updated_at: new Date().toISOString()
      };

      if (editingPrompt) {
        const { error } = await supabase
          .from('prompts')
          .update(promptData)
          .eq('id', editingPrompt.id);

        if (error) throw error;
        toast.success('Prompt updated successfully');
      } else {
        const { error } = await supabase
          .from('prompts')
          .insert({
            ...promptData,
            created_at: new Date().toISOString()
          });

        if (error) throw error;
        toast.success('Prompt created successfully');
      }

      setShowModal(false);
      loadPrompts();
    } catch (error: any) {
      console.error('Error saving prompt:', error);
      toast.error(error.message || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promptId: string, promptName: string) => {
    if (!confirm(`Are you sure you want to delete the prompt "${promptName}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('prompts')
        .delete()
        .eq('id', promptId);

      if (error) throw error;

      toast.success('Prompt deleted successfully');
      loadPrompts();
    } catch (error: any) {
      console.error('Error deleting prompt:', error);
      toast.error('Failed to delete prompt');
    }
  };

  const toggleActive = async (promptId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('prompts')
        .update({
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', promptId);

      if (error) throw error;

      toast.success(`Prompt ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadPrompts();
    } catch (error: any) {
      console.error('Error toggling prompt status:', error);
      toast.error('Failed to update prompt status');
    }
  };

  const handleExport = async () => {
    try {
      const { exportPromptsConfiguration } = await import('../../utils/configurationExporter');
      const result = await exportPromptsConfiguration();

      if (result.success) {
        toast.success(`Exported ${result.filename} successfully!`);
      } else {
        toast.error(result.error || 'Failed to export prompts');
      }
    } catch (error: any) {
      console.error('Error exporting:', error);
      toast.error('Failed to export prompts');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Prompt Management</h1>
            <p className="text-gray-600">Manage AI prompts and their execution order</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleExport} variant="outline" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export Prompts
            </Button>
            <Button onClick={handleCreateNew} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Prompt
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search prompts by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Output Variable
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrompts.map((prompt) => (
                <tr key={prompt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {prompt.execution_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{prompt.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-md truncate">
                      {prompt.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {prompt.output_variable || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActive(prompt.id, prompt.is_active)}
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                        prompt.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {prompt.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(prompt)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(prompt.id, prompt.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredPrompts.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No prompts found</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingPrompt ? 'Edit Prompt' : 'Create New Prompt'}
        size="xl"
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label htmlFor="execution_order" className="block text-sm font-medium text-gray-700 mb-1">
                Execution Order
              </label>
              <Input
                id="execution_order"
                type="number"
                value={formData.execution_order}
                onChange={(e) => setFormData({ ...formData, execution_order: parseInt(e.target.value) })}
                min="1"
              />
            </div>

            <div>
              <label htmlFor="output_variable" className="block text-sm font-medium text-gray-700 mb-1">
                Output Variable
              </label>
              <Input
                id="output_variable"
                type="text"
                value={formData.output_variable}
                onChange={(e) => setFormData({ ...formData, output_variable: e.target.value })}
                placeholder="e.g., analysis_result"
              />
            </div>

            <div className="col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={2}
              />
            </div>

            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                  Prompt Content *
                </label>
                <button
                  type="button"
                  onClick={() => setShowVariables(!showVariables)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {showVariables ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showVariables ? 'Hide' : 'Show'} Available Variables
                </button>
              </div>

              {showVariables && (
                <div className="mb-3 p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Personas</h4>
                      <div className="flex flex-wrap gap-1">
                        {personas.map((persona) => (
                          <button
                            key={persona}
                            type="button"
                            onClick={() => insertVariable(persona)}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                            title={`Insert {${persona}}`}
                          >
                            {persona}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Context Variables</h4>
                      <div className="space-y-3">
                        {contextVariables?.categories && Object.entries(contextVariables.categories).map(([category, vars]: [string, any]) => (
                          <div key={category}>
                            <div className="text-xs font-medium text-gray-600 mb-1.5 capitalize">{category.replace('_', ' ')}</div>
                            <div className="flex flex-wrap gap-1">
                              {vars.map((variable: string) => (
                                <button
                                  key={variable}
                                  type="button"
                                  onClick={() => insertVariable(variable)}
                                  className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
                                  title={`Insert {${variable}}`}
                                >
                                  {variable}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-3 border-t pt-2">Click any variable to insert it at cursor position</p>
                    </div>
                  </div>
                </div>
              )}

              <textarea
                ref={contentTextareaRef}
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                rows={10}
                required
              />
            </div>

            <div className="col-span-2">
              <label htmlFor="depends_on_steps" className="block text-sm font-medium text-gray-700 mb-1">
                Dependencies (JSON Array)
              </label>
              <textarea
                id="depends_on_steps"
                value={formData.depends_on_steps}
                onChange={(e) => setFormData({ ...formData, depends_on_steps: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                rows={3}
                placeholder='["step1", "step2"]'
              />
            </div>

            <div className="col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : editingPrompt ? 'Update Prompt' : 'Create Prompt'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminPrompts;
